<template>
  <div></div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted, shallowRef } from 'vue'
import * as THREE from 'three'
import type { DevParam } from './types'

interface Props {
  devParam?: DevParam | null
}

const props = withDefaults(defineProps<Props>(), {
  devParam: null,
})

const hasSensor = ref(false)
const z = computed(() => (props.devParam && props.devParam?.z_cm / 100 + 0.03) || 0)
// 使用shallowRef避免Vue深度代理Three.js对象
const beamsGroup = shallowRef<THREE.Group | null>(null)
const beamMaterial = shallowRef<THREE.ShaderMaterial | null>(null)
const animationId = ref<number | null>(null)

// 导出beamsGroup，供父组件使用
defineExpose({
  beamsGroup,
})

// 着色器定义
const radarScanBeamShaders = {
  vertex: /* glsl */ `
    attribute float aSeed;
    attribute float aAlpha;
    attribute float aWidth;

    varying float vSeed;
    varying float vAlpha;
    varying float vWidth;
    varying float vPosZ;
    varying vec3 vPosition;

    void main() {
      vSeed = aSeed;
      vAlpha = aAlpha;
      vWidth = aWidth;
      vPosZ = position.z;
      vPosition = position;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragment: /* glsl */ `
    precision highp float;

    uniform float uTime;
    uniform vec3 uColor;
    uniform float uGlobalIntensity;
    uniform float uPulseSpeed;
    uniform float uPulseScale;
    uniform float uNoiseFreq;

    varying float vSeed;
    varying float vAlpha;
    varying float vWidth;
    varying float vPosZ;
    varying vec3 vPosition;

    float hash(float x) {
      return fract(sin(x) * 43758.5453123);
    }

    float noise1(float x) {
      float i = floor(x);
      float f = fract(x);
      float a = hash(i);
      float b = hash(i + 1.0);
      float u = f*f*(3.0 - 2.0*f);
      return mix(a, b, u);
    }

    float smoothNoise(float x) {
      return
        noise1(x) * 0.5 +
        noise1(x * 1.7 + 10.0) * 0.25 +
        noise1(x * 3.1 + 4.0) * 0.25;
    }

    float gauss(float x, float sigma) {
      return exp(-(x*x) / (2.0*sigma*sigma));
    }

    void main() {
      float radial = length(vPosition.xy);
      float normRad = radial / 3.0;
      float t = clamp((-vPosZ) / 2.5, 0.0, 1.0);

      float verticalFade = 1.0 - smoothstep(0.0, 1.0, t);
      float edge = smoothstep(vWidth, vWidth * 0.02, normRad);

      float n = smoothNoise(vSeed * 6.0 + uTime * uNoiseFreq);
      float flicker = (0.7 + n * 0.6) * (0.6 + 0.4 * t);

      float pulsePos = fract(uTime * uPulseSpeed * (0.2 + 0.8 * vSeed));
      float pulse = gauss(abs(t - pulsePos), 0.06) * uPulseScale;

      float base = vAlpha * verticalFade * edge * uGlobalIntensity;
      float alpha = base * flicker + pulse * base;

      float glow = gauss(normRad, 0.45);
      vec3 col = vec3(0.13, 1.0, 0.8) * (0.6 + glow * 0.8);

      gl_FragColor = vec4(col, alpha);
      if (gl_FragColor.a < 0.001) discard;
    }
  `,
}

// 可调参数
const BEAM_COUNT = 100
const HEIGHT = 2.5
const RADIUS = 3.0
const ROT_SPEED = 0.01

// 创建光束组
const createBeamsGroup = () => {
  // 创建着色器材质
  beamMaterial.value = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
    uniforms: {
      uTime: { value: 0 },
      uColor: { value: new THREE.Color(0x22ffcc) },
      uGlobalIntensity: { value: 0.8 },
      uPulseSpeed: { value: 0.45 },
      uPulseScale: { value: 1.5 },
      uNoiseFreq: { value: 0.1 },
    },
    vertexShader: radarScanBeamShaders.vertex,
    fragmentShader: radarScanBeamShaders.fragment,
  })

  const group = new THREE.Group()

  for (let i = 0; i < BEAM_COUNT; i++) {
    const angle = Math.random() * Math.PI * 2
    const widthAngle = 0.01 + Math.random() * 0.06
    const baseAlpha = 0.04 + Math.random() * 0.18
    const seed = Math.random()

    const left = new THREE.Vector3(
      Math.cos(angle - widthAngle) * RADIUS,
      Math.sin(angle - widthAngle) * RADIUS,
      -HEIGHT
    )
    const right = new THREE.Vector3(
      Math.cos(angle + widthAngle) * RADIUS,
      Math.sin(angle + widthAngle) * RADIUS,
      -HEIGHT
    )

    const positions = new Float32Array([0, 0, 0, left.x, left.y, left.z, right.x, right.y, right.z])

    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute(
      'aSeed',
      new THREE.BufferAttribute(new Float32Array([seed, seed, seed]), 1)
    )
    geometry.setAttribute(
      'aAlpha',
      new THREE.BufferAttribute(
        new Float32Array([baseAlpha, baseAlpha * 0.25, baseAlpha * 0.08]),
        1
      )
    )
    geometry.setAttribute(
      'aWidth',
      new THREE.BufferAttribute(
        new Float32Array([
          0.02 + Math.random() * 0.08,
          0.02 + Math.random() * 0.08,
          0.02 + Math.random() * 0.08,
        ]),
        1
      )
    )

    group.add(new THREE.Mesh(geometry, beamMaterial.value))
  }

  // 设置光束组位置
  group.position.set(0, 0, z.value)
  beamsGroup.value = group
}

// 动画循环
const animate = () => {
  animationId.value = requestAnimationFrame(animate)

  if (hasSensor.value) {
    // 旋转光束组 - 确保beamsGroup和rotation存在
    if (
      beamsGroup.value &&
      beamsGroup.value.rotation &&
      beamsGroup.value.rotation.z !== undefined
    ) {
      beamsGroup.value.rotation.z += ROT_SPEED
    }
  }
}

onMounted(() => {
  createBeamsGroup()
  animate()
})

onUnmounted(() => {
  // 停止动画
  if (animationId.value) {
    cancelAnimationFrame(animationId.value)
  }

  // 清理资源
  if (beamsGroup.value) {
    beamsGroup.value.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose()
      }
    })
  }
  if (beamMaterial.value) {
    beamMaterial.value.dispose()
  }
})
</script>

<style scoped lang="less"></style>
